import type { LucideIcon } from 'lucide-react';
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Award,
  BarChart3,
  Barcode,
  Bookmark,
  Boxes,
  Brush,
  Calculator,
  Calendar,
  ClipboardCheck,
  ClipboardList,
  CloudDownload,
  Coins,
  Cog,
  CreditCard,
  DollarSign,
  Download,
  Edit,
  FileText,
  Flame,
  Folder,
  HandCoins,
  HardDrive,
  Heart,
  Languages,
  Layers,
  LayoutDashboard,
  Lock,
  Mail,
  MailOpen,
  Monitor,
  Package,
  PackagePlus,
  PieChart,
  Receipt,
  Repeat,
  RotateCcw,
  Scale,
  ShieldAlert,
  Shield,
  ShoppingBag,
  ShoppingCart,
  Smartphone,
  Sliders,
  Store,
  Tag,
  TrendingDown,
  TrendingUp,
  Truck,
  TruckIcon,
  User,
  UserCheck,
  UserSquare,
  UserX,
  Users,
  Warehouse,
  Workflow,
} from 'lucide-react';

export interface NavItem {
  label: string;
  i18nKey?: string;
  to?: string;
  icon?: LucideIcon;
  permission?: string | string[];
  children?: NavItem[];
  /** When true the item renders as a non-clickable section header inside a dropdown. */
  header?: boolean;
}

export interface NavGroup {
  group: string;
  i18nKey?: string;
  /** Icon shown next to the group title in the sidebar header. */
  icon?: LucideIcon;
  items: NavItem[];
  /**
   * If set, the group renders as a single direct link (no collapsible).
   * `items` is ignored in that case.
   */
  to?: string;
  /** Permission required to see a standalone (`to`) group. */
  permission?: string | string[];
}

export const navConfig: NavGroup[] = [
  {
    group: 'Dashboard',
    i18nKey: 'nav.dashboard',
    icon: LayoutDashboard,
    to: '/app/dashboard',
    items: [],
  },
  {
    group: 'User Management',
    i18nKey: 'nav.user_management',
    icon: Shield,
    items: [
      { label: 'Users', i18nKey: 'nav.users', to: '/app/users', icon: Users, permission: 'users_view' },
      {
        label: 'Group Permissions',
        i18nKey: 'nav.roles',
        to: '/app/roles',
        icon: Shield,
        permission: 'permissions_view',
      },
    ],
  },
  {
    group: 'Products',
    i18nKey: 'nav.products_group',
    icon: Package,
    items: [
      {
        label: 'Create product',
        i18nKey: 'nav.products_new',
        to: '/app/products/new',
        icon: PackagePlus,
        permission: 'products_add',
      },
      {
        label: 'All Products',
        i18nKey: 'nav.products',
        to: '/app/products',
        icon: Package,
        permission: 'products_view',
      },
      {
        label: 'Import products',
        i18nKey: 'nav.products_import',
        to: '/app/products/import',
        icon: Download,
        permission: 'product_import',
      },
      {
        label: 'Import (Update Only)',
        i18nKey: 'nav.products_import_update',
        to: '/app/products/import-update',
        icon: Download,
        permission: 'product_import',
      },
      {
        label: 'Opening Stock',
        i18nKey: 'nav.opening_stock',
        to: '/app/products/opening-stock',
        icon: Boxes,
        permission: 'opening_stock_import',
      },
      {
        label: 'Print Labels',
        i18nKey: 'nav.print_labels',
        to: '/app/products/print-labels',
        icon: Barcode,
        permission: 'barcode_view',
      },
      {
        label: 'Count Stock',
        i18nKey: 'nav.count_stock',
        to: '/app/count-stock',
        icon: ClipboardCheck,
        permission: 'count_stock',
      },
      { label: 'Category', i18nKey: 'nav.categories', to: '/app/categories', icon: Layers, permission: 'category' },
      {
        label: 'Sub Category',
        i18nKey: 'nav.subcategories',
        to: '/app/subcategories',
        icon: Layers,
        permission: 'subcategory',
      },
      { label: 'Brand', i18nKey: 'nav.brands', to: '/app/brands', icon: Layers, permission: 'brand' },
      { label: 'Unit', i18nKey: 'nav.units', to: '/app/units', icon: Scale, permission: 'unit' },
    ],
  },
  {
    group: 'Sales',
    i18nKey: 'nav.sales',
    icon: Receipt,
    items: [
      {
        label: 'Create Sale',
        i18nKey: 'nav.sales_new',
        to: '/app/sales/new',
        icon: PackagePlus,
        permission: 'Sales_add',
      },
      {
        label: 'Import Sales',
        i18nKey: 'nav.sales_import',
        to: '/app/sales/import',
        icon: Download,
        permission: 'Sales_add',
      },
      {
        label: 'All Sales',
        i18nKey: 'nav.sales_list',
        to: '/app/sales',
        icon: Receipt,
        permission: 'Sales_view',
      },
      { label: 'POS', i18nKey: 'nav.pos', to: '/app/pos', icon: ShoppingCart, permission: 'Pos_view' },
      {
        label: 'Customer Screen',
        i18nKey: 'nav.customer_screen',
        to: '/app/customer-display',
        icon: Monitor,
        permission: 'customer_display_screen_setup',
      },
      {
        label: 'Shipments',
        i18nKey: 'nav.shipments',
        to: '/app/shipments',
        icon: Truck,
        permission: 'shipment',
      },
      {
        label: 'Quotations',
        i18nKey: 'nav.quotations',
        to: '/app/quotations',
        icon: ClipboardList,
        permission: 'Quotations_view',
      },
      {
        label: 'Sales Returns',
        i18nKey: 'nav.sale_returns',
        to: '/app/sale-returns',
        icon: RotateCcw,
        permission: 'Sale_Returns_view',
      },
    ],
  },
  {
    group: 'Purchases',
    i18nKey: 'nav.purchases',
    icon: Truck,
    items: [
      {
        label: 'Create Purchase',
        i18nKey: 'nav.purchases_new',
        to: '/app/purchases/new',
        icon: PackagePlus,
        permission: 'Purchases_add',
      },
      {
        label: 'All Purchases',
        i18nKey: 'nav.purchases_list',
        to: '/app/purchases',
        icon: Truck,
        permission: 'Purchases_view',
      },
      {
        label: 'Import Purchases',
        i18nKey: 'nav.purchases_import',
        to: '/app/purchases/import',
        icon: Download,
        permission: 'Purchases_add',
      },
      {
        label: 'Purchase Returns',
        i18nKey: 'nav.purchase_returns',
        to: '/app/purchase-returns',
        icon: RotateCcw,
        permission: 'Purchase_Returns_view',
      },
    ],
  },
  {
    group: 'Inventory',
    i18nKey: 'nav.inventory',
    icon: Boxes,
    items: [
      { label: 'Adjustments', i18nKey: 'nav.adjustments', to: '/app/adjustments', icon: Boxes, permission: 'adjustment_view' },
      { label: 'Damages', i18nKey: 'nav.damages', to: '/app/damages', icon: Boxes, permission: 'damage_view' },
      { label: 'Transfers', i18nKey: 'nav.transfers', to: '/app/transfers', icon: TruckIcon, permission: 'transfer_view' },
      { label: 'Warehouses', i18nKey: 'nav.warehouses', to: '/app/warehouses', icon: Warehouse, permission: 'warehouse' },
    ],
  },
  {
    group: 'People',
    i18nKey: 'nav.people',
    icon: Users,
    items: [
      { label: 'Customers', i18nKey: 'nav.customers', to: '/app/customers', icon: Users, permission: 'Customers_view' },
      {
        label: 'Customers with login',
        i18nKey: 'nav.customers_ecommerce',
        to: '/app/customers/ecommerce',
        icon: Store,
        permission: 'Customers_view',
      },
      {
        label: 'Customers without login',
        i18nKey: 'nav.customers_without_ecommerce',
        to: '/app/customers/without-ecommerce',
        icon: UserX,
        permission: 'Customers_view',
      },
      {
        label: 'Import customers',
        i18nKey: 'nav.customers_import',
        to: '/app/customers/import',
        icon: Download,
        permission: 'customers_import',
      },
      { label: 'Suppliers', i18nKey: 'nav.suppliers', to: '/app/suppliers', icon: UserSquare, permission: 'Suppliers_view' },
      {
        label: 'Import suppliers',
        i18nKey: 'nav.suppliers_import',
        to: '/app/suppliers/import',
        icon: Download,
        permission: 'Suppliers_import',
      },
    ],
  },
  {
    group: 'Accounting',
    i18nKey: 'nav.accounting',
    icon: Calculator,
    items: [
      { label: 'Dashboard', i18nKey: 'nav.accounting_dashboard', to: '/app/accounting/dashboard', icon: Calculator, permission: 'accounting_dashboard' },
      { label: 'Chart of Accounts', i18nKey: 'nav.coa', to: '/app/accounting/chart-of-accounts', icon: Calculator, permission: 'chart_of_accounts' },
      { label: 'Journal Entries', i18nKey: 'nav.journal_entries', to: '/app/accounting/journal-entries', icon: Calculator, permission: 'journal_entries' },
      { label: 'Trial Balance', i18nKey: 'accounting.trial_balance.title', to: '/app/accounting/reports/trial-balance', icon: Calculator, permission: 'trial_balance' },
      { label: 'Profit & Loss', i18nKey: 'accounting.profit_loss.title', to: '/app/accounting/reports/profit-loss', icon: Calculator, permission: 'accounting_profit_loss' },
      { label: 'Balance Sheet', i18nKey: 'accounting.balance_sheet.title', to: '/app/accounting/reports/balance-sheet', icon: Calculator, permission: 'balance_sheet' },
      { label: 'Tax Summary', i18nKey: 'accounting.tax_report.title', to: '/app/accounting/reports/tax-summary', icon: Calculator, permission: 'accounting_tax_report' },
      { label: 'Accounts', i18nKey: 'accounting.accounts_list', to: '/app/accounts', icon: CreditCard, permission: 'account' },
      { label: 'Transfer Money', i18nKey: 'accounting.transfer_money', to: '/app/transfer-money', icon: HandCoins, permission: 'transfer_money' },
      { label: 'Expenses', i18nKey: 'nav.expenses', to: '/app/expenses', icon: CreditCard, permission: 'expense_view' },
      { label: 'Expense Categories', i18nKey: 'accounting.expense_categories', to: '/app/expenses/categories', icon: CreditCard, permission: 'expense_view' },
      { label: 'Deposits', i18nKey: 'nav.deposits', to: '/app/deposits', icon: CreditCard, permission: 'deposit_view' },
      { label: 'Deposit Categories', i18nKey: 'accounting.deposit_categories', to: '/app/deposits/categories', icon: CreditCard, permission: 'deposit_view' },
    ],
  },
  {
    group: 'Online Store',
    i18nKey: 'nav.online_store',
    icon: Store,
    items: [
      { label: 'Settings', i18nKey: 'store.settings', to: '/app/store/settings', icon: Store, permission: 'Store_settings_view' },
      { label: 'Orders', i18nKey: 'nav.online_orders', to: '/app/store/orders', icon: Store, permission: 'Orders_view' },
      { label: 'Pages', i18nKey: 'nav.store_pages', to: '/app/store/pages', icon: Store, permission: 'Store_settings_view' },
      { label: 'Banners', i18nKey: 'store.banners', to: '/app/store/banners', icon: Store, permission: 'Banners_view' },
      { label: 'Collections', i18nKey: 'store.collections', to: '/app/store/collections', icon: Store, permission: 'Collections_view' },
      { label: 'Subscribers', i18nKey: 'store.subscribers', to: '/app/store/subscribers', icon: Users, permission: 'Subscribers_view' },
      { label: 'Messages', i18nKey: 'store.messages', to: '/app/store/messages', icon: Store, permission: 'Messages_view' },
    ],
  },
  {
    group: 'Reports',
    i18nKey: 'nav.reports',
    icon: BarChart3,
    // Order exactly matches the user-specified list (mirroring the legacy POS sidebar).
    items: [
      // Payments group header (non-clickable) + the 4 payment links
      { label: 'Payments', i18nKey: 'report.payments_group', header: true },
      { label: 'Purchase payments', i18nKey: 'report.payments_purchase', to: '/app/reports/payments-purchase', icon: HandCoins },
      { label: 'Sales payments', i18nKey: 'report.payments_sale', to: '/app/reports/payments-sale', icon: HandCoins },
      { label: 'Sales-return payments', i18nKey: 'report.payments_sales_returns', to: '/app/reports/payments-sales-returns', icon: HandCoins },
      { label: 'Purchase-return payments', i18nKey: 'report.payments_purchase_returns', to: '/app/reports/payments-purchase-returns', icon: HandCoins },
      // Rest of the legacy order
      { label: 'Transactions report', i18nKey: 'report.transactions', to: '/app/reports/transactions', icon: DollarSign },
      { label: 'Cash flow', i18nKey: 'report.cash_flow', to: '/app/reports/cash-flow', icon: DollarSign },
      { label: 'Seller report', i18nKey: 'report.seller', to: '/app/reports/seller', icon: User },
      { label: 'Attendance summary', i18nKey: 'report.attendance', to: '/app/reports/attendance', icon: UserCheck },
      { label: 'Profit & Loss', i18nKey: 'report.profit_and_loss', to: '/app/reports/profit-and-loss', icon: BarChart3 },
      { label: 'Return ratio', i18nKey: 'report.return_ratio', to: '/app/reports/return-ratio', icon: Repeat },
      { label: 'Cash register report', i18nKey: 'report.cash_register', to: '/app/reports/cash-register', icon: CreditCard },
      { label: 'Warranty & guarantee', i18nKey: 'report.warranty_guarantee', to: '/app/reports/warranty-guarantee', icon: Flame },
      { label: 'Inventory valuation', i18nKey: 'report.inventory_valuation', to: '/app/reports/inventory-valuation', icon: Coins },
      { label: 'Analytics', i18nKey: 'report.analytics', to: '/app/reports/analytics', icon: BarChart3 },
      { label: 'Stock inventory valuation', i18nKey: 'report.stock_inventory_valuation', to: '/app/reports/stock-inventory-valuation', icon: Coins },
      { label: 'Expenses report', i18nKey: 'report.expenses', to: '/app/reports/expenses', icon: ArrowUpRight },
      { label: 'Deposits report', i18nKey: 'report.deposits', to: '/app/reports/deposits', icon: ArrowDownRight },
      { label: 'Warehouse report', i18nKey: 'report.warehouse', to: '/app/reports/warehouse', icon: Warehouse },
      { label: 'Stock report', i18nKey: 'report.stock', to: '/app/reports/stock', icon: Boxes },
      { label: 'Negative stock', i18nKey: 'report.negative_stock', to: '/app/reports/negative-stock', icon: TrendingDown },
      { label: 'Quantity alerts', i18nKey: 'report.quantity_alerts', to: '/app/reports/quantity-alerts', icon: ShieldAlert },
      { label: 'Purchase report', i18nKey: 'report.purchases', to: '/app/reports/purchases', icon: Truck },
      { label: 'Sales report', i18nKey: 'report.sales', to: '/app/reports/sales', icon: Receipt },
      { label: 'Product sales report', i18nKey: 'report.product_sales', to: '/app/reports/product-sales', icon: ShoppingBag },
      { label: 'Product purchases', i18nKey: 'report.product_purchases', to: '/app/reports/product-purchases', icon: ShoppingBag },
      { label: 'Suppliers report', i18nKey: 'report.suppliers', to: '/app/reports/suppliers', icon: Truck },
      { label: 'Top suppliers', i18nKey: 'report.top_suppliers', to: '/app/reports/top-suppliers', icon: Award },
      { label: 'Customers report', i18nKey: 'report.customers', to: '/app/reports/customers', icon: Users },
      { label: 'Top selling products', i18nKey: 'report.top_selling_products', to: '/app/reports/top-selling-products', icon: TrendingUp },
      { label: 'Inactive customers', i18nKey: 'report.inactive_customers', to: '/app/reports/inactive-customers', icon: Users },
      { label: 'Top customers', i18nKey: 'report.top_customers', to: '/app/reports/top-customers', icon: Award },
      { label: 'Users report', i18nKey: 'report.users', to: '/app/reports/users', icon: Users },
      { label: 'Login activity', i18nKey: 'report.login_activity', to: '/app/reports/login-activity', icon: Lock },
      { label: 'Product report', i18nKey: 'report.product', to: '/app/reports/product', icon: Tag },
      { label: 'Zero-sales products', i18nKey: 'report.zero_sales', to: '/app/reports/zero-sales', icon: TrendingDown },
      { label: 'Dead stock', i18nKey: 'report.dead_stock', to: '/app/reports/dead-stock', icon: AlertTriangle },
      { label: 'Stock aging', i18nKey: 'report.stock_aging', to: '/app/reports/stock-aging', icon: Calendar },
      { label: 'Stock transfer', i18nKey: 'report.stock_transfer', to: '/app/reports/stock-transfer', icon: Repeat },
      { label: 'Stock adjustment', i18nKey: 'report.stock_adjustment', to: '/app/reports/stock-adjustment', icon: Edit },
      { label: 'Discount summary', i18nKey: 'report.discount_summary', to: '/app/reports/discount-summary', icon: PieChart },
      { label: 'Customer loyalty points', i18nKey: 'report.loyalty_points', to: '/app/reports/loyalty-points', icon: Heart },
      { label: 'Tax summary', i18nKey: 'report.tax_summary', to: '/app/reports/tax-summary', icon: PieChart },
      { label: 'Draft invoices', i18nKey: 'report.draft_invoices', to: '/app/reports/draft-invoices', icon: FileText },
      { label: 'Sales by category', i18nKey: 'report.sales_by_category', to: '/app/reports/sales-by-category', icon: Folder },
      { label: 'Sales by brand', i18nKey: 'report.sales_by_brand', to: '/app/reports/sales-by-brand', icon: Bookmark },
      { label: 'Error logs', i18nKey: 'report.error_logs', to: '/app/reports/error-logs', icon: ShieldAlert },
      { label: 'Service jobs report', i18nKey: 'report.service_jobs', to: '/app/reports/service-jobs', icon: Receipt },
      { label: 'Checklist completion', i18nKey: 'report.service_checklist', to: '/app/reports/service-checklist', icon: FileText },
      { label: 'Customer maintenance history', i18nKey: 'report.customer_maintenance_history', to: '/app/reports/customer-maintenance-history', icon: Calendar },
    ],
  },
  {
    group: 'Settings',
    i18nKey: 'nav.settings',
    icon: Cog,
    items: [
      { label: 'System Settings', i18nKey: 'sidebar.system_settings', to: '/app/settings/system', icon: Cog, permission: 'setting_system' },
      { label: 'Quickbooks Sync', i18nKey: 'sidebar.quickbooks_sync', to: '/app/settings/quickbooks_sync', icon: Workflow, permission: 'quickbooks_settings' },
      { label: 'Dynamic Appearance', i18nKey: 'sidebar.dynamic_appearance', to: '/app/settings/appearance_settings', icon: Brush, permission: 'appearance_settings' },
      { label: 'Languages', i18nKey: 'sidebar.languages', to: '/app/settings/translations', icon: Languages, permission: 'translations_settings' },
      { label: 'Payment Methods', i18nKey: 'sidebar.payment_methods', to: '/app/settings/payment-methods', icon: CreditCard, permission: 'payment_methods' },
      { label: 'Sms Settings', i18nKey: 'sidebar.sms_settings', to: '/app/settings/sms_settings', icon: Smartphone, permission: 'sms_settings' },
      { label: 'SMS Templates', i18nKey: 'sidebar.sms_templates', to: '/app/settings/sms_templates', icon: FileText, permission: 'notification_template' },
      { label: 'Mail Settings', i18nKey: 'sidebar.mail_settings', to: '/app/settings/mail_settings', icon: Mail, permission: 'mail_settings' },
      { label: 'Email Templates', i18nKey: 'sidebar.email_templates', to: '/app/settings/email_templates', icon: MailOpen, permission: 'notification_template' },
      { label: 'POS Settings', i18nKey: 'sidebar.pos_settings', to: '/app/settings/pos_settings', icon: Sliders, permission: 'pos_settings' },
      { label: 'POS Receipt', i18nKey: 'sidebar.pos_receipt', to: '/app/settings/pos_receipt', icon: Receipt, permission: 'pos_settings' },
      { label: 'Module Settings', i18nKey: 'sidebar.module_settings', to: '/app/settings/module_settings', icon: Boxes, permission: 'module_settings' },
      { label: 'Upgrade', i18nKey: 'sidebar.upgrade', to: '/app/settings/update_settings', icon: CloudDownload, permission: 'update_settings' },
      { label: 'Payment Gateway', i18nKey: 'sidebar.payment_gateway', to: '/app/settings/payment_gateway', icon: CreditCard, permission: 'payment_gateway' },
      { label: 'Warehouse', i18nKey: 'sidebar.warehouse', to: '/app/settings/Warehouses', icon: Warehouse, permission: 'warehouse' },
      { label: 'Currency', i18nKey: 'sidebar.currency', to: '/app/settings/Currencies', icon: Coins, permission: 'currency' },
      { label: 'Backup', i18nKey: 'sidebar.backup', to: '/app/settings/Backup', icon: HardDrive, permission: 'backup' },
      { label: 'Login Device Management', i18nKey: 'sidebar.login_device_management', to: '/app/settings/login_devices', icon: Shield, permission: 'login_device_management' },
    ],
  },
];
